import {
  createContext,
  type FC,
  type PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

import {
  type UserOrderResponseV2,
  type UserSubOrderV2,
} from '@redteclab/api/clients/bully'

import { useApiOrdersSwrGetLastOrder } from '../../../order'

type ContactPageLastOrderContextValue = {
  activeSubOrder?: UserSubOrderV2
  firstSubOrder?: UserSubOrderV2
  lastOrder?: UserOrderResponseV2
  setActiveSubOrder?: (subOrder: UserSubOrderV2) => void
  totalItems?: number
}

const ContactPageLastOrderContext =
  createContext<ContactPageLastOrderContextValue | null>(null)

export const useContactPageLastOrderContext =
  (): ContactPageLastOrderContextValue => {
    const context = useContext(ContactPageLastOrderContext)
    if (!context) {
      throw new Error('No ContactPageLastOrderContext')
    }

    return context
  }

export const ContactPageLastOrderProvider: FC<PropsWithChildren> = ({
  children,
}) => {
  const { data: lastOrder } = useApiOrdersSwrGetLastOrder()
  const firstSubOrder = lastOrder?.subOrders?.[0]
  const totalItems = lastOrder?.totalOrderItems

  const [activeSubOrder, setActiveSubOrder] = useState<
    UserSubOrderV2 | undefined
  >(firstSubOrder)

  useEffect(() => {
    // Reset active suborder when order changes
    // eslint-disable-next-line react-hooks/set-state-in-effect -- don't reference, refactor if touched
    setActiveSubOrder(firstSubOrder)
  }, [firstSubOrder])

  const value = useMemo(
    () => ({
      activeSubOrder,
      firstSubOrder,
      lastOrder,
      setActiveSubOrder,
      totalItems,
    }),
    [firstSubOrder, lastOrder, totalItems, activeSubOrder],
  )

  return (
    <ContactPageLastOrderContext.Provider value={value}>
      {children}
    </ContactPageLastOrderContext.Provider>
  )
}
